import React, { type ReactNode } from 'react';
import { type CardProps as AntdCardProps } from 'antd';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { typeStyle } from 'constants/typography';
import CommonCard from 'components/common/card';

/**
 * antd's surface plus the props the wrapper lifted off it. Derivations pass `bodyPadding` where they used
 * to write an `.ant-card-body` rule, so the bridge has to accept it.
 */
export type BridgedCardProps = AntdCardProps & { bodyPadding?: string };

/**
 * The seam between antd's Card prop surface and the wrapper's — the one call site to adapt if they
 * diverge.
 *
 * `bordered={false}` has no direct equivalent: the wrapper's `inline` drops the box border but adds a
 * bottom rule unless `inlineBottomBorder` suppresses it, so borderless needs both. Only
 * `bodyStyle.padding` is carried across, because that is the only declaration any call site passes —
 * cloud-resource-modal-summary's `{ padding: '12px' }`. Widen this if another site passes more, rather
 * than dropping declarations silently.
 */
export const toCommonProps = (props: BridgedCardProps) => {
  const { bodyStyle, bordered, hoverable, cover, title, children, ...rest } = props;

  const fromBodyStyle = typeof bodyStyle?.padding === 'string' ? bodyStyle.padding : undefined;

  return {
    ...rest,
    title,
    children,
    hoverable,
    cover,
    // Spread conditionally rather than assigned: a plain `bodyPadding: fromBodyStyle` overwrites the
    // `bodyPadding` a derivation passes through `...rest` with `undefined` whenever there is no
    // `bodyStyle`, silently dropping it.
    ...(fromBodyStyle ? { bodyPadding: fromBodyStyle } : {}),
    ...(bordered === false ? { inline: true, inlineBottomBorder: true } : {})
  };
};

/**
 * Takes antd's Card props for the call sites written against antd, and renders the common wrapper.
 * Moving those sites onto the wrapper's own props removes this adapter.
 */
export const BridgedCard: React.FC<BridgedCardProps> = props => {
  return <CommonCard {...toCommonProps(props)} />;
};

type CardMetaProps = {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
};

/**
 * antd scopes every `.ant-card-meta*` rule as `.ant-card .ant-card-meta*` - card/style/index.js nests the
 * meta block inside `[componentCls]` - and `Meta` itself has no `useStyle`, only `getPrefixCls` and
 * classNames. BridgedCard renders the wrapper's plain div and no `.ant-card` ever mounts, so antd injects
 * nothing and the avatar/title/description would lose their layout entirely. Being inside the card body is
 * not enough; the CSS needs the ancestor.
 *
 * Hence a real seam rather than a re-export: the layout comes from tokens here. Class names mirror antd's
 * shape without borrowing its prefix, matching how the wrapper names `custom-card-body`.
 *
 * Values track antd's genCardMetaStyle: flex row, avatar gutter, detail column takes the remainder, title
 * at the 16px heading style, description in secondary.
 */
const MetaRoot = styled.div`
  display: flex;
`;

const MetaAvatar = styled.div`
  ${({ theme }) => css`
    padding-inline-end: ${theme.spacing.lg};
  `}
`;

const MetaDetail = styled.div`
  overflow: hidden;
  flex: 1;

  ${({ theme }) => css`
    > *:not(:last-child) {
      margin-bottom: ${theme.spacing.xs};
    }
  `}
`;

const MetaTitle = styled.div`
  ${typeStyle.headingH4}
  color: var(--text-heading);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MetaDescription = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-secondary);
`;

export const CardMeta: React.FC<CardMetaProps> = ({ avatar, title, description, className }) => {
  return (
    <MetaRoot className={`card-meta${className ? ` ${className}` : ''}`}>
      {avatar && <MetaAvatar className="card-meta-avatar">{avatar}</MetaAvatar>}
      {(title || description) && (
        <MetaDetail className="card-meta-detail">
          {title && <MetaTitle className="card-meta-title">{title}</MetaTitle>}
          {description && <MetaDescription className="card-meta-description">{description}</MetaDescription>}
        </MetaDetail>
      )}
    </MetaRoot>
  );
};

export default BridgedCard;
